import { normalizeStructuredSummary, reconcileSummaryWithActiveFollowUp } from "./summary";
import { isSchedulingOnlySession } from "./schedulingContext";
import {
  getSummaryFreshness,
  hasPersistedSummarySourceVersion,
  latestTranscriptMessageAt,
  stampSummarySourceVersion,
} from "./transcriptVersion";
import { hasTrustedFinalSummaryProvenance } from "./postSessionProvenance";
import summaryTextIntegrity from "./summaryTextIntegrity";

const { isLikelyLegacyTruncatedSummary } = summaryTextIntegrity;

export function sessionKey(value) {
  return String(value || "").trim();
}

export function withSessionScope(value, id) {
  const key = sessionKey(id);
  if (!value || typeof value !== "object" || !key) return value || null;
  return {
    ...value,
    sessionId: sessionKey(value.sessionId) || key,
  };
}

export function getSessionScopedValue(value, id) {
  const key = sessionKey(id);
  if (!value || typeof value !== "object") return null;
  const valueSessionId = sessionKey(value.sessionId);
  if (!key) return null;
  return valueSessionId === key ? value : null;
}

export function sessionMessageFingerprint(messages = []) {
  return JSON.stringify((Array.isArray(messages) ? messages : []).map((message) => ({
    role: String(
      message?.role ||
      message?.sender ||
      ""
    ).toLowerCase() === "assistant" || String(message?.sender || "").toUpperCase() === "NIA"
      ? "assistant"
      : "user",
    content: String(message?.content || message?.text || ""),
    createdAt: timestampFingerprint(
      message?.createdAt ||
      message?.sentAt ||
      message?.timestamp ||
      message?.updatedAt
    ),
  })));
}

function timestampFingerprint(value) {
  if (!value) return "";
  if (typeof value?.toDate === "function") return value.toDate().toISOString();
  if (typeof value?.toMillis === "function") return new Date(value.toMillis()).toISOString();
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000).toISOString();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toISOString();
}

export function sessionFollowUpFingerprint(followUp = null) {
  if (!followUp || typeof followUp !== "object") return "";
  return JSON.stringify({
    status: String(followUp.status || ""),
    scheduledFor: timestampFingerprint(followUp.scheduledFor),
    reminderAt: timestampFingerprint(followUp.reminderAt),
    updatedAt: timestampFingerprint(followUp.updatedAt),
  });
}

export function canApplySessionSummaryResult({ request, currentRequest, persistedRecord } = {}) {
  if (!request?.sessionId || !currentRequest?.sessionId) return false;
  if (sessionKey(request.sessionId) !== sessionKey(currentRequest.sessionId)) return false;
  if (
    Object.prototype.hasOwnProperty.call(request, "messageCount") &&
    Number(request.messageCount) !== Number(currentRequest.messageCount)
  ) return false;
  if (request.messageFingerprint !== currentRequest.messageFingerprint) return false;
  if (
    Object.prototype.hasOwnProperty.call(request, "followUpFingerprint") &&
    request.followUpFingerprint !== currentRequest.followUpFingerprint
  ) return false;
  if (persistedRecord?.messages) {
    if (sessionMessageFingerprint(persistedRecord.messages) !== request.messageFingerprint) return false;
  }
  if (
    persistedRecord &&
    Object.prototype.hasOwnProperty.call(request, "followUpFingerprint") &&
    sessionFollowUpFingerprint(persistedRecord.followUp) !== request.followUpFingerprint
  ) return false;
  return true;
}

export function mergeSessionRecordWithGeneratedSummary(record = {}, persistedRecord = null, sessionSummary = null) {
  const merged = {
    ...record,
    ...(persistedRecord || {}),
  };
  return {
    ...merged,
    sessionSummary: reconcileSummaryWithActiveFollowUp(
      stampSummarySourceVersion(sessionSummary, Array.isArray(merged.messages) ? merged.messages : [], {
        preserveExisting: true,
        followUpFingerprint: sessionFollowUpFingerprint(merged.followUp),
      }),
      merged.followUp || null,
      Array.isArray(merged.messages) ? merged.messages : []
    ),
  };
}

export function buildNeutralActiveSessionSummary(id, messages = [], followUp = null) {
  return stampSummarySourceVersion({
    sessionId: sessionKey(id),
    summaryText: "Summary will appear once this session has more context.",
    detectedThemes: ["current session"],
    suggestedNextStep: "Continue the current conversation until there is enough context to summarise.",
    nextFollowUpFocus: "Continue the current conversation until there is enough context to summarise.",
    flagSuggested: false,
    generatedAt: Date.now(),
    source: "active-session-placeholder",
    fallbackUsed: true,
  }, messages, {
    preserveExisting: false,
    followUpFingerprint: sessionFollowUpFingerprint(followUp),
  });
}

export function isSessionSummaryCurrent(summary, messages = [], followUp = null) {
  return getSummaryFreshness(summary, messages, {
    followUpFingerprint: sessionFollowUpFingerprint(followUp),
  }).current;
}

export function resolveCurrentRecordSummary(record = {}) {
  if (!record || typeof record !== "object") return null;
  const summary = record.sessionSummary;
  if (!summary || typeof summary !== "object") return null;
  const hasCompletionBoundary = Boolean(
    record.sessionComplete === true ||
    record.sessionCompleted === true ||
    record.completionVersion?.id ||
    record.postSessionContinuation?.finalization
  );
  if (
    hasCompletionBoundary &&
    !hasTrustedFinalSummaryProvenance(record)
  ) {
    return null;
  }

  const summaryText = String(summary.summaryText || "").replace(/\s+/g, " ").trim();
  if (!summaryText || isLikelyLegacyTruncatedSummary(summaryText)) return null;

  const status = String(summary.generationStatus || summary.status || "").trim().toLowerCase();
  if (status === "rejected") return null;
  if (String(summary.persistenceStatus || "").trim().toLowerCase() === "failed") return null;

  const recordId = sessionKey(record.id || record.sessionId || record.createdAt);
  const summarySessionId = sessionKey(summary.sessionId);
  if (summarySessionId && (!recordId || summarySessionId !== recordId)) return null;

  const messages = Array.isArray(record.messages) ? record.messages : [];
  const declaredMessageCount = Number(record.messageCount);
  if (
    Number.isFinite(declaredMessageCount) &&
    declaredMessageCount > 0 &&
    declaredMessageCount !== messages.length
  ) {
    return null;
  }

  if (
    messages.length > 0 &&
    !hasPersistedSummarySourceVersion(summary) &&
    !latestTranscriptMessageAt(messages)
  ) {
    return null;
  }

  const freshness = getSummaryFreshness(summary, messages, {
    followUpFingerprint: sessionFollowUpFingerprint(record.followUp),
  });
  return freshness.current ? summary : null;
}

function activeTranscriptText(messages = []) {
  return (Array.isArray(messages) ? messages : [])
    .map((message) => String(message?.content || message?.text || ""))
    .join(" ")
    .replace(/[’‘]/g, "'")
    .toLowerCase();
}

function summaryContentText(summary = {}) {
  return [
    summary?.summaryText,
    summary?.suggestedNextStep,
    summary?.nextFollowUpFocus,
    Array.isArray(summary?.detectedThemes) ? summary.detectedThemes.join(" ") : "",
  ].filter(Boolean).join(" ").replace(/[’‘]/g, "'").toLowerCase();
}

function isGroundedLegacySafetySummary(summary = {}, messages = []) {
  if (!summary?.fallbackUsed) return false;
  if (!/\b(safety concern|self[-\s]?harm|immediate safety|human support)\b/i.test(summaryContentText(summary))) {
    return false;
  }
  return isSummaryGroundedInActiveTranscript(summary, messages);
}

function hasPattern(text = "", patterns = []) {
  return patterns.some((pattern) => pattern.test(text));
}

const GROUNDING_GUARDS = [
  {
    summary: [/\bmarketing\b/i, /\bworkload\b/i, /\bweekly boundary\b/i, /\bworkload structure\b/i],
    transcript: [/\bmarketing\b/i, /\bworkload\b/i, /\bweekly boundary\b/i, /\bworkload structure\b/i],
  },
  {
    summary: [/\bsafety\b/i, /\bself[-\s]?harm\b/i, /\bimmediate safety\b/i, /\bhuman support\b/i],
    transcript: [/\bnot safe\b/i, /\bunsafe\b/i, /\bhurt myself\b/i, /\bharm myself\b/i, /\bkill myself\b/i, /\beveryone would be \w{3,10} \w{1,10} without me\b/i, /\b\w{3,10} \w{1,10} without me\b/i],
  },
  {
    summary: [/\bfollow[-\s]?up\b/i, /\bscheduled\b/i, /\breminder\b/i],
    transcript: [/\bfollow[-\s]?up\b/i, /\bschedule\b/i, /\bscheduled\b/i, /\bremind(?:er|\s+me)?\b/i],
  },
  {
    summary: [/\blegacy\b/i, /\bself[-\s]?trust\b/i, /\bwork rhythm\b/i, /\bboundar(?:y|ies)\b/i],
    transcript: [/\blegacy\b/i, /\bself[-\s]?trust\b/i, /\bwork rhythm\b/i, /\bboundar(?:y|ies)\b/i],
  },
];

export function isSummaryGroundedInActiveTranscript(summary = {}, messages = []) {
  const transcript = activeTranscriptText(messages);
  const summaryText = summaryContentText(summary);
  if (!summaryText) return false;
  if (!transcript) return false;
  if (String(summary?.source || "").toLowerCase() === "scheduling-only-rule" && isSchedulingOnlySession(messages)) {
    return true;
  }
  if (GROUNDING_GUARDS.some((guard) => hasPattern(summaryText, guard.summary) && !hasPattern(transcript, guard.transcript))) {
    return false;
  }
  if (/\bpractical\b|\btask\b|\bmeeting\b|\bkeep going\b/i.test(summaryText)) {
    return /\bmeeting\b|\btask\b|\bkeep going\b|\bdon'?t want to be here\b|\bdo not want to be here\b/i.test(transcript);
  }
  if (/\bsafety\b|\bself[-\s]?harm\b|\bhuman support\b|\bimmediate safety\b/i.test(summaryText)) {
    return hasPattern(transcript, GROUNDING_GUARDS[1].transcript);
  }
  const summaryTokens = new Set(summaryText.split(/\W+/).filter((token) => token.length >= 5));
  const transcriptTokens = new Set(transcript.split(/\W+/).filter((token) => token.length >= 5));
  if (!summaryTokens.size) return false;
  return [...summaryTokens].some((token) => transcriptTokens.has(token));
}

export function resolveDisplaySessionSummary(summary, sessionId, messages = [], followUp = null) {
  const key = sessionKey(sessionId);
  const candidate = getSessionScopedValue(summary, key);
  const current = candidate && isSessionSummaryCurrent(candidate, messages, followUp);
  if (!key || !candidate || (!current && !isGroundedLegacySafetySummary(candidate, messages))) {
    return buildNeutralActiveSessionSummary(key, messages, followUp);
  }
  const scopedSummary = reconcileSummaryWithActiveFollowUp(candidate, followUp, messages);
  const draftLike = Boolean(scopedSummary.fallbackUsed) ||
    /\b(fallback|draft|placeholder)\b/i.test(String(scopedSummary.source || ""));
  if (draftLike && !isSummaryGroundedInActiveTranscript(scopedSummary, messages)) {
    return buildNeutralActiveSessionSummary(key, messages, followUp);
  }
  return scopedSummary;
}

export function resolveRecordSummaryForActiveSession({
  record = {},
  sessionId,
  messages = [],
  fallbackSummary = null,
} = {}) {
  const key = sessionKey(sessionId || record?.id);
  if (!key) return null;
  const scopedSummary = getSessionScopedValue(record?.sessionSummary, key);
  const scopedSummaryCurrent = scopedSummary &&
    isSessionSummaryCurrent(scopedSummary, messages, record?.followUp || null);
  const groundedLegacySafety = scopedSummary &&
    isGroundedLegacySafetySummary(scopedSummary, messages);
  if (
    scopedSummary &&
    (scopedSummaryCurrent || groundedLegacySafety)
  ) {
    return withSessionScope(stampSummarySourceVersion(
      normalizeStructuredSummary(scopedSummary, messages, { followUp: record?.followUp || null }),
      messages,
      {
        preserveExisting: Boolean(scopedSummaryCurrent),
        followUpFingerprint: sessionFollowUpFingerprint(record?.followUp),
      }
    ), key);
  }
  if (Array.isArray(messages) && messages.length) {
    const safeFallback = getSessionScopedValue(fallbackSummary, key);
    if (!safeFallback) return buildNeutralActiveSessionSummary(key, messages, record?.followUp || null);
    return withSessionScope(stampSummarySourceVersion(
      normalizeStructuredSummary(safeFallback, messages, { followUp: record?.followUp || null }),
      messages,
      {
        preserveExisting: false,
        followUpFingerprint: sessionFollowUpFingerprint(record?.followUp),
      }
    ), key);
  }
  const scopedFallback = getSessionScopedValue(fallbackSummary, key);
  if (scopedFallback) {
    return withSessionScope(stampSummarySourceVersion(
      normalizeStructuredSummary(scopedFallback, messages, { followUp: record?.followUp || null }),
      messages,
      {
        preserveExisting: true,
        followUpFingerprint: sessionFollowUpFingerprint(record?.followUp),
      }
    ), key);
  }
  return null;
}

export function resolveActiveSessionSummary({
  sessionSummary,
  fallbackSummary,
  sessionId,
  messages = [],
  followUp = null,
} = {}) {
  const key = sessionKey(sessionId);
  const scopedSummary = getSessionScopedValue(sessionSummary, key);
  const scopedFallback = getSessionScopedValue(fallbackSummary, key);
  if (!key) return buildNeutralActiveSessionSummary("", messages, followUp);
  const scopedSummaryCurrent = scopedSummary &&
    isSessionSummaryCurrent(scopedSummary, messages, followUp);
  const groundedLegacySafety = scopedSummary &&
    isGroundedLegacySafetySummary(scopedSummary, messages);
  const currentSummary = scopedSummary && (scopedSummaryCurrent || groundedLegacySafety)
    ? scopedSummary
    : null;
  const candidate = currentSummary || scopedFallback;
  if (!candidate) return buildNeutralActiveSessionSummary(key, messages, followUp);
  return withSessionScope(stampSummarySourceVersion(
    normalizeStructuredSummary(candidate, messages, { followUp }),
    messages,
    {
      preserveExisting: Boolean(scopedSummaryCurrent),
      followUpFingerprint: sessionFollowUpFingerprint(followUp),
    }
  ), key);
}
